/**
 * Standalone SVG of the 2D floor plan — literally the PlanSvg component tree
 * the editor renders, in export mode (no grid, hit strips, handles, or dim
 * bars), wrapped in a fitted <svg> and serialized. Because the same components
 * draw both, the export always matches the editor stroke for stroke.
 */

import { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import {
  ALL_PLAN_LAYERS,
  createEditorState,
  fitViewport,
  type PlanLayers,
  type Viewport,
} from '@kaiizen/kai-floor-plan-editor-core';
import { renderToStaticMarkup } from 'react-dom/server';
import { canvasBg } from '../../styles';
import { PlanExportContext } from './planExportContext';
import { PlanSvg } from './PlanSvg';

/** Nominal rendering scale of an exported plan, before margin fitting. */
export const EXPORT_PX_PER_FOOT = 40;
export const EXPORT_MARGIN_PX = 40;

export interface PlanSvgExport {
  svg: string;
  /** The projection used — screenToModel(viewport, …) maps export px back to plan feet. */
  viewport: Viewport;
  width: number;
  height: number;
}

/** The plan rendered for export, plus the projection serializers need to invert it. */
export const renderPlanExport = (room: FloorPlanRoom, layers: PlanLayers = ALL_PLAN_LAYERS): PlanSvgExport => {
  const width = Math.round(room.width * EXPORT_PX_PER_FOOT + 2 * EXPORT_MARGIN_PX);
  const height = Math.round(room.length * EXPORT_PX_PER_FOOT + 2 * EXPORT_MARGIN_PX);
  const st = createEditorState(room);
  st.layers = layers;
  st.viewport = fitViewport(room, width, height, st.viewport);
  const svg = renderToStaticMarkup(
    <PlanExportContext.Provider value={true}>
      <svg xmlns='http://www.w3.org/2000/svg' width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <rect width={width} height={height} fill={canvasBg} />
        <PlanSvg st={st} stageW={width} stageH={height} />
      </svg>
    </PlanExportContext.Provider>,
  );
  return { svg, viewport: st.viewport, width, height };
};

export const roomToPlanSvg = (room: FloorPlanRoom, layers: PlanLayers = ALL_PLAN_LAYERS): string => renderPlanExport(room, layers).svg;
